import type { CustomFile } from '../../types';

import { allows, toAllowList, type TypeAllowList } from './allowList';
import { errorMessages } from './errorMessages';

import type { TypeValidateFiles } from './types';

// ----------------------------------------------------------------------------

/*
 * The configured rules, compiled once for the whole pass. Building the allow
 * lists per file would rebuild the same three sets ten thousand times over.
 */
export type TypeFileRules = {
    accept: TypeAllowList | null;
    extensions: TypeAllowList | null;
    types: TypeAllowList | null;
    size?: number;
    acceptUnknown: boolean;
};

// ----------------------------------------------------------------------------

function compileRules(config: TypeValidateFiles): TypeFileRules {
    return {
        accept: toAllowList(config.accept),
        extensions: toAllowList(config.extensions),
        types: toAllowList(config.types),
        size: config.size,
        acceptUnknown: Boolean(config.acceptUnknown),
    };
}

// ----------------------------------------------------------------------------

/*
 * Every reason the built-in rules have against this file, or an empty list where
 * they have none. Nothing is decided here and nothing is written: what an empty
 * list means — accepted, or merely not rejected yet — is for the pass to say,
 * once the custom validator has had its turn too.
 *
 * Reads file.extension, which the pass fills in from the name beforehand.
 */
function checkFile(file: CustomFile, rules: TypeFileRules): string[] {
    const errors: string[] = [];

    // Size stands apart: it holds whatever the file is called.
    if (rules.size != null && file.size > rules.size) {
        errors.push(errorMessages.size(file, rules.size));
    }

    const extension = (file.extension ?? '').toLowerCase();
    const type = file.type.toLowerCase();

    /*
     * A file with no extension carries nothing for the lists to match against, so
     * whether it counts is the caller's call rather than a rejection by default.
     */
    if (rules.acceptUnknown && extension.trim() === '') {
        return errors;
    }

    if (rules.accept && !allows(rules.accept, extension) && !allows(rules.accept, type)) {
        errors.push(errorMessages.accept(file));
    }

    if (rules.extensions && !allows(rules.extensions, extension)) {
        errors.push(errorMessages.extension(file));
    }

    if (rules.types && !allows(rules.types, type)) {
        errors.push(errorMessages.type(file));
    }

    return errors;
}

// ----------------------------------------------------------------------------

export { checkFile, compileRules };
